import _ from "lodash";
import React, { useState } from "react";

import { ALL_STATES } from "@/constants/states";
import Button from "@/components/UI/Button";
import { Button2 } from "@/ui2/atoms/Button";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import DatePicker from "@/components/UI/DatePicker";
import dayjs from "@/lib/helpers/dayjs";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { Hint } from "@/ui2/atoms/Hint";
import images from "@/utils/images";
import { isUserAdmin } from "@/utils/authToken";
import { Link2 } from "@/ui2/atoms/Link";
import { Portal } from "@/components/Common/Modal/Portal";

export function StatesToPractice(props) {
  const {
    commitAndSubmitStateLicensureDataToServer,
    dateContext,
    isEditable,
    showUpdateStateModal,
    states_to_practice,
    toggleStateModal,
  } = props;

  const [uncommitedStatesToPractice, setUncommitedStatesToPractice] = useState(
    _.cloneDeep(states_to_practice),
  );

  const updateStateValue = (index) => (e) => {
    const newUncommitedStatesToPractice = _.cloneDeep(uncommitedStatesToPractice);

    newUncommitedStatesToPractice[index].state = e.target.value;
    setUncommitedStatesToPractice(newUncommitedStatesToPractice);
  };

  const addAuthorizedState = () => {
    const newState = {
      displayDatepicker: null,
      expirationDate: null,
      licenseNumber: null,
      state: "New York",
    };

    if (_.isNil(uncommitedStatesToPractice) || _.isEmpty(uncommitedStatesToPractice)) {
      setUncommitedStatesToPractice([newState]);
    } else {
      // Slightly odd edge case: make sure every node has a visibility setting (this is not stored in db)
      const cleanStatesToPractice = _.cloneDeep(uncommitedStatesToPractice).map((stateToPractice) =>
        resetDatepickerClosed(stateToPractice),
      );

      // If the list already exists, create a new with the the placeholder newState on the end
      cleanStatesToPractice.push(newState);
      setUncommitedStatesToPractice(cleanStatesToPractice);
    }
  };

  const updateStateLicenseNumber = (index) => (e) => {
    const newUncommitedStatesToPractice = _.cloneDeep(uncommitedStatesToPractice);

    newUncommitedStatesToPractice[index].licenseNumber = e.target.value;
    setUncommitedStatesToPractice(newUncommitedStatesToPractice);
  };

  const markDatepickerAsClosed = (index) => () => {
    const newUncommitedStatesToPractice = _.cloneDeep(uncommitedStatesToPractice);

    newUncommitedStatesToPractice[index]["displayDatepicker"] = null;
    setUncommitedStatesToPractice(newUncommitedStatesToPractice);
  };

  // Required as someone can bail from the modal and not "commit" the datepicker changes
  const updateStateLicenseExpirationFromDatePicker = (index, value) => {
    const newUncommitedStatesToPractice = _.cloneDeep(uncommitedStatesToPractice);

    newUncommitedStatesToPractice[index].dateToCommit = value;
    setUncommitedStatesToPractice(newUncommitedStatesToPractice);
  };

  const commitDate = (index) => {
    const newUncommitedStatesToPractice = [...uncommitedStatesToPractice];

    newUncommitedStatesToPractice[index].expirationDate =
      newUncommitedStatesToPractice[index].dateToCommit;
    setUncommitedStatesToPractice(newUncommitedStatesToPractice);
  };

  const toggleDatepicker = (index) => {
    // Only toggle the specific display attribute of the stateOfPractice (selected by index)
    const cleanStatesToPractice = _.cloneDeep(uncommitedStatesToPractice).map((stateToPractice) =>
      resetDatepickerClosed(stateToPractice),
    );
    const newStateOfInterest = cleanStatesToPractice[index];

    // Reverse the state of the datepicker for that state (hide/show modal for that state datepicker)
    newStateOfInterest["displayDatepicker"] = !newStateOfInterest["displayDatepicker"];
    // Set updated element in list
    cleanStatesToPractice[index] = newStateOfInterest;
    setUncommitedStatesToPractice(cleanStatesToPractice);
  };

  const deleteStateLicense = (index) => () => {
    const cleanStatesToPractice = _.cloneDeep(uncommitedStatesToPractice)
      .map((stateToPractice) => resetDatepickerClosed(stateToPractice))
      .filter((_stateLicenseData, listIndex) => listIndex !== index);

    setUncommitedStatesToPractice(cleanStatesToPractice);
  };

  const updateStateAreaProps = {
    commitDate,
    dateContext,
    deleteStateLicense,
    markDatepickerAsClosed,
    states_to_practice: uncommitedStatesToPractice,
    toggleDatepicker,
    updateStateLicenseExpirationFromDatePicker,
    updateStateLicenseNumber,
    updateStateValue,
  };

  const isAdmin = isUserAdmin();

  return (
    <DashboardCard2
      Actions={
        isAdmin && (
          <Button2
            onPress={() => toggleStateModal()}
            size="sm"
            variant="link"
            {...getAutomationDataAttr("AuthorizedStatesToPracticeEditButton")}
          >
            Edit
          </Button2>
        )
      }
      title="Authorized States to Practice"
    >
      <div className="ProviderProfile__states-to-practice">
        <ul className="ProviderProfile__insurance-info-list fs-14">
          <Portal
            headerRender={
              <div className="fs-22 mb-5 text-center tw:font-bold">
                Authorized States to Practice
              </div>
            }
            isBottomCloseActive={false}
            isOpenOnRender={false}
            onModalClose={() => setUncommitedStatesToPractice(states_to_practice)}
            triggerModalStateChange={showUpdateStateModal}
          >
            <UpdateStateArea {...updateStateAreaProps} />

            <br />

            <div className="row">
              <div className="col-md-12">
                <Button
                  className="Btn Btn--otl-gry Btn--sm-2 workflow-btn float-left"
                  onClick={() => addAuthorizedState()}
                  type="button"
                  {...(isAdmin &&
                    getAutomationDataAttr("AuthorizedStatesToPracticeAddStateButton"))}
                >
                  Add State
                </Button>

                <Button
                  className="Btn Btn--pri Btn--sm-2 workflow-btn float-right"
                  onClick={() => {
                    commitAndSubmitStateLicensureDataToServer(uncommitedStatesToPractice);
                  }}
                  type="submit"
                  {...(isAdmin && getAutomationDataAttr("AuthorizedStatesToPracticeSaveButton"))}
                >
                  Save
                </Button>
              </div>
            </div>
          </Portal>
        </ul>

        {isEditable && licensedStateDisplayArea(uncommitedStatesToPractice)}

        {!isAdmin && isEditable && (
          <Hint>
            To update authorized states, contact{" "}
            <Link2 href="mailto:support@aptihealth.com" isExternal size="xs">
              support@aptihealth.com
            </Link2>
            .
          </Hint>
        )}
      </div>
    </DashboardCard2>
  );
}

/**
 * Resets the Datepicker visibility
 * The Portal element is designed to be visible when an attribute is true and not visible
 * when in null | false state
 * For historical reasons, the DatePicker must instantiate with null and not false
 * In a complex workflow such this component with lists of Datepickers, to prevent
 * prior datepickers from being rendered on a re-render, it is easier to set all datepickers
 * to closed via null attribute
 * This approach will be used any time a datepicker from the list of datepickers (embedded in a modal)
 * are invoked or interacted with
 */
const resetDatepickerClosed = (stateToPractice) => {
  const clonedStateToPractice = _.cloneDeep(stateToPractice);

  clonedStateToPractice["displayDatepicker"] = null;

  return clonedStateToPractice;
};

const UpdateStateArea = (props) => {
  const {
    commitDate,
    dateContext,
    deleteStateLicense,
    markDatepickerAsClosed,
    states_to_practice,
    toggleDatepicker,
    updateStateLicenseExpirationFromDatePicker,
    updateStateLicenseNumber,
    updateStateValue,
  } = props;

  if (_.isNil(states_to_practice)) {
    return;
  }

  const stateRowProps = {
    commitDate,
    dateContext,
    deleteStateLicense,
    markDatepickerAsClosed,
    toggleDatepicker,
    updateStateLicenseExpirationFromDatePicker,
    updateStateLicenseNumber,
    updateStateValue,
  };

  return (
    <>
      {states_to_practice.map((stateOfPractice, index) => (
        <StateRow index={index} key={index} stateOfPractice={stateOfPractice} {...stateRowProps} />
      ))}
    </>
  );
};

const StateRow = ({ index, stateOfPractice, ...props }) => {
  const { displayDatepicker, expirationDate, licenseNumber, state } = stateOfPractice;

  const {
    commitDate,
    dateContext,
    deleteStateLicense,
    markDatepickerAsClosed,
    toggleDatepicker,
    updateStateLicenseExpirationFromDatePicker,
    updateStateLicenseNumber,
    updateStateValue,
  } = props;

  const testId = `StateToPractice${index}`;
  const selectTestId = `${testId}Select`;

  return (
    <>
      <div className="row" key={`stateLicense-${index}`}>
        <div className="col-md-3">
          <label htmlFor={`state-${index}`}>
            <b>State</b>
          </label>

          <select
            className="form-control"
            id={`state-${index}`}
            key={`state-${index}`}
            onChange={updateStateValue(index)}
            value={state}
            {...getAutomationDataAttr(selectTestId)}
          >
            {ALL_STATES.map((s) => makeStateInDropdown(s, selectTestId))}
          </select>
        </div>

        <div className="col-md-3">
          <label htmlFor={`licenseNumber-${index}`}>
            <b>License Number</b>
          </label>

          <input
            className="form-control"
            id={`licenseNumber-${index}`}
            onChange={updateStateLicenseNumber(index)}
            placeholder="Enter license number"
            value={licenseNumber}
            {...getAutomationDataAttr(`${testId}LicenseNumber`)}
          />
        </div>

        <div className="col-md-3">
          <label style={redTextIfExpired(expirationDate)}>
            <b>Expiration Date</b>
            {maybeExclamationGlyphicon(expirationDate)}
          </label>

          <input
            className="form-control"
            readOnly
            style={redTextIfExpired(expirationDate)}
            value={expirationDate}
          />
        </div>

        <div className="col-md-2">
          <br />

          <DatepickerModal
            commitDate={commitDate}
            dateContext={dateContext}
            displayDatepicker={displayDatepicker}
            index={index}
            markDatepickerAsClosed={markDatepickerAsClosed}
            state={state}
            toggleDatepicker={toggleDatepicker}
            updateStateLicenseExpirationFromDatePicker={updateStateLicenseExpirationFromDatePicker}
          />
        </div>

        <div className="col-md-1">
          <div
            className="mx-auto mt-3 d-flex align-item-center cursor tw:justify-center"
            style={{ paddingTop: "12px" }}
          >
            <button
              className="btn-reset"
              onClick={deleteStateLicense(index)}
              type="button"
              {...getAutomationDataAttr(`${testId}DeleteLicense`)}
            >
              <img alt="Delete license" src={images("./icons/delete-trash.svg")} />
            </button>
          </div>
        </div>
      </div>
      <br />
    </>
  );
};

const DatepickerModal = ({ index, state, ...props }) => {
  const {
    commitDate,
    dateContext,
    displayDatepicker,
    markDatepickerAsClosed,
    toggleDatepicker,
    updateStateLicenseExpirationFromDatePicker,
  } = props;

  return (
    <Portal
      headerRender={<b>{`Please Select Expiration Date for ${state} License`}</b>}
      isBottomCloseActive={false}
      isOpenOnRender={false}
      onModalClose={markDatepickerAsClosed(index)}
      openButtonRender={
        <img alt="message" src={images("./icons/common/schedule/schedule-ter-none-2.svg")} />
      }
      testId={`StateToPractice${index}SetExpirationDate`}
      triggerModalStateChange={displayDatepicker}
    >
      <DatePicker
        externalCtx={dateContext}
        loadingSlots={false}
        onDaySelect={(date) =>
          updateStateLicenseExpirationFromDatePicker(index, convertYyyyMmDdToMmDdYyyy(date))
        }
      />

      <Button
        className="Btn Btn--pri Btn--sm-2 workflow-btn float-right"
        onClick={() => {
          commitDate(index);
          toggleDatepicker(index);
        }}
        type="submit"
        {...getAutomationDataAttr(`StateToPractice${index}ConfirmExpirationDate`)}
      >
        Confirm Date
      </Button>
    </Portal>
  );
};

const licensedStateDisplayArea = (states) => {
  if (_.isNil(states) || (_.isEmpty(states) && isUserAdmin())) {
    return <Hint>Add Authorized States with the Edit Button</Hint>;
  }

  // Design calls for rows of 2 (left and right)
  // This function breaks the states into a structure representing the row
  // Example [{stateData}, {stateData}, {stateData}] => [[{stateData}, {stateData}], [{stateData}]]
  const rowData = _.chunk(states, 2);

  return (
    <>
      {rowData.map((data, index) => {
        const [leftColumnState, rightColumnState] = data;

        return makeStateDisplayRow(leftColumnState, rightColumnState, index);
      })}
    </>
  );
};

const makeStateDisplayRow = (leftColumnState, rightColumnState, index) => {
  return (
    <div
      className="row"
      key={`row-index-${index}`}
      {...getAutomationDataAttr(`AuthorizedStateToPracticeRow${index}`)}
    >
      {makeSingleStateDisplayArea(leftColumnState)}
      {makeSingleStateDisplayArea(rightColumnState)}
    </div>
  );
};

const makeSingleStateDisplayArea = (state) => {
  if (_.isNil(state)) {
    return;
  }
  const licenseNumberToDisplay = state.licenseNumber || "";
  const expirationToDisplay = state.expirationDate || "";

  return (
    <div className="col-md-6">
      {/* Indentation madness */}
      <div className="row" style={{ fontWeight: "bold", paddingLeft: "5px" }}>
        {`• ${state.state}`}
      </div>
      <div className="row">
        <div className="col-md-6">{`License Number: ${licenseNumberToDisplay}`}</div>
        <div className="col-md-6" style={redTextIfExpired(state.expirationDate)}>
          {`Exp: ${expirationToDisplay}`}
        </div>
      </div>
      <br />
    </div>
  );
};

const makeStateInDropdown = (state, testId) => {
  return (
    <option
      key={state.display}
      {...getAutomationDataAttr(`${testId}${state.display.replaceAll(" ", "")}`)}
    >
      {state.display}
    </option>
  );
};

const redTextIfExpired = (mmddyyyyDate) => {
  if (isLicensedExpired(mmddyyyyDate)) {
    return { color: "red" };
  }

  return {};
};

const maybeExclamationGlyphicon = (mmddyyyyDate) => {
  if (isLicensedExpired(mmddyyyyDate)) {
    return (
      <>
        <span className="glyphicon glyphicon-exclamation-sign"></span>
      </>
    );
  }
};

const isLicensedExpired = (mmddyyyyDate) => {
  return dayjs() >= dayjs(mmddyyyyDate, "MM/DD/YYYY");
};

const convertYyyyMmDdToMmDdYyyy = (str) => {
  const [year, month, day] = str.split("-");

  return `${month}/${day}/${year}`;
};
